import { captureException } from '@sentry/vue';

import { TripPlannerPax } from '@apps/main/modules/recipe/pax';

import type {
  ExposedRecipe,
  OldExposedRecipe,
  ExposedIngredient,
  OldExposedIngredient,
  AvailabilityOrder,
} from '@libs/trip-planner-interfaces';
import {
  AvailabilityOrderQL,
  IngredientType,
} from '@libs/trip-planner-interfaces';

import { isOldExposedFlightIngredient } from './recipeSerializer.flight.helpers';
import { isOldExposedHotelIngredient } from './recipeSerializer.hotel.helpers';

const MAX_DECODE_ITERATIONS = 5;

function iterativeDecodeURIComponent(input: string): {
  decoded: string;
  iterations: number;
} {
  let current = input;
  let iterations = 0;

  while (iterations < MAX_DECODE_ITERATIONS) {
    let next: string;
    try {
      next = decodeURIComponent(current);
    } catch {
      break;
    }
    iterations++;
    if (next === current) {
      break;
    }
    current = next;
  }

  return { decoded: current, iterations };
}

export function encodeRecipe(recipeJSON: ExposedRecipe): string {
  return window.btoa(encodeURIComponent(JSON.stringify(recipeJSON)));
}

export function decodeRecipe(
  encodedRecipe: string
): ExposedRecipe | OldExposedRecipe | null {
  // NOTE:
  // Default parseQuery function from vue-router replaces '+' sign with space (check source code).
  // This breaks query parameters which are base64 encoded.
  // Space is not base64 valid character, hence replacing it back fixes the problem without breaking encoding.
  encodedRecipe = encodedRecipe.replace(/ /g, '+');

  const { decoded: decodedInput, iterations } =
    iterativeDecodeURIComponent(encodedRecipe);

  if (iterations >= MAX_DECODE_ITERATIONS) {
    captureException(
      new Error(
        `Exceeded maximum decode iterations (${iterations}). Possible infinite encoding loop.`
      ),
      { extra: { encodedRecipe, iterations } }
    );
    return null;
  }

  let recipeJSON: ExposedRecipe | OldExposedRecipe;
  try {
    recipeJSON = JSON.parse(decodeURIComponent(window.atob(decodedInput)));
    if (typeof recipeJSON === 'string') {
      recipeJSON = JSON.parse(recipeJSON);
    }
    return recipeJSON;
  } catch {
    try {
      recipeJSON = JSON.parse(window.atob(decodedInput));
      if (typeof recipeJSON === 'string') {
        recipeJSON = JSON.parse(recipeJSON);
      }
      return recipeJSON;
    } catch (error) {
      captureException(new Error('Invalid encoded recipe'), {
        extra: {
          encodedRecipe,
          iterations,
          parseError: String(error),
        },
      });
      return null;
    }
  }
}

export function isOldExposedRecipe(
  exposedRecipe: ExposedRecipe | OldExposedRecipe
): exposedRecipe is OldExposedRecipe {
  const areOldIngredients = exposedRecipe.ingredients.some(
    (ingredient: ExposedIngredient | OldExposedIngredient) => {
      if (ingredient.ingredientType === IngredientType.Hotel) {
        return isOldExposedHotelIngredient(ingredient);
      } else if (ingredient.ingredientType === IngredientType.Flight) {
        return isOldExposedFlightIngredient(ingredient);
      }
      return false;
    }
  );
  const isOldPax = TripPlannerPax.isOldPax(exposedRecipe.pax);

  return areOldIngredients || isOldPax || 'suggestionSortType' in exposedRecipe;
}

export function isAvailabilityOrderQl(
  availabilityOrder: AvailabilityOrder | AvailabilityOrderQL
): availabilityOrder is AvailabilityOrderQL {
  return Object.values(AvailabilityOrderQL).includes(
    availabilityOrder as AvailabilityOrderQL
  );
}
